import type { FunctionComponent } from 'preact';
import { useMemo } from 'preact/hooks';

import type { RepeaterFunctionInvoker, TemplateFunction, TemplateResult } from 'Components/types';
import { discard, discardQuery, filterQueryResetRequested, toggleColor } from 'Core/actions/request';
import { createFilteredResponseSelectionSelector } from 'Core/selectors/search';
import { useSelector } from 'Core/store';
import { FacetValue, type FacetValueFull } from 'Models/index';
import { DisplayMode } from 'Modules/serverApi/types';

import Chip, { type Params as ChipParams, type Props as ChipProps } from './chip';

export interface Params {
  chips: RepeaterFunctionInvoker<ChipParams>;
}

interface Props {
  template: TemplateFunction<Params>;
  fields?: string[];
  ignoreFields?: string[];
}

const FilterChips: FunctionComponent<Props> = ({ template, fields, ignoreFields }) => {
  const selection = (
    useSelector(
      useMemo(() => createFilteredResponseSelectionSelector(fields, ignoreFields), [fields, ignoreFields]),
    ) as FacetValueFull[]
  ).filter((field) => field.displayMode !== DisplayMode.None);

  if (!selection.length) {
    return null;
  }

  const chipMap: Record<string, FacetValueFull[]> = {};
  for (const value of selection) {
    chipMap[value.field] ??= [];
    chipMap[value.field].push(value);
  }

  const chips = Object.values(chipMap)
    .flat()
    .map((value) => (templ: TemplateFunction<ChipParams>) => {
      // Each branch ensures value and discardValue are consistent at runtime; cast to the unified Props type.
      const discardValue: ChipProps['discardValue'] =
        value.field === 'query'
          ? (_value, options) => discardQuery(options)
          : value.field === 'filterQuery'
            ? (_value, options) => filterQueryResetRequested(options)
            : FacetValue.isColor(value)
              ? toggleColor
              : discard;
      const extendedProps = {
        template: templ,
        value,
        discardValue,
      };
      return (<Chip {...extendedProps} key={FacetValue.termKey(value)} />) as TemplateResult;
    });

  return template.call({ chips });
};

export default FilterChips;
